/**
 * The player's record, kept on the device.
 *
 * Deliberately local-only. The plan's identity model is an anonymous device
 * account with no email and no forced sign-in, so there is no server-side
 * profile to write to yet — and a record that silently needed an account would
 * contradict that. When accounts arrive this becomes the local half of a sync,
 * not something to throw away.
 *
 * Storage failures are swallowed everywhere. localStorage throws in private
 * mode on some browsers, and a stat strip is never worth breaking a game over.
 */

const KEY = 'p11.record';

export interface Record {
  played: number;
  won: number;
  tied: number;
  /** Highest total the user's side has ever posted. */
  best: number;
}

const EMPTY: Record = { played: 0, won: 0, tied: 0, best: 0 };

export function loadRecord(): Record {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<Record>;
    // Read each field defensively: this is user-writable storage, and a
    // hand-edited or half-written value should degrade to zero rather than
    // render "NaN%" on the home screen forever.
    const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0);
    return {
      played: num(parsed.played),
      won: num(parsed.won),
      tied: num(parsed.tied),
      best: num(parsed.best),
    };
  } catch {
    return { ...EMPTY };
  }
}

export function recordMatch(result: { won: boolean; tied: boolean; scoreFor: number }): Record {
  const next = loadRecord();
  next.played++;
  if (result.tied) next.tied++;
  else if (result.won) next.won++;
  next.best = Math.max(next.best, result.scoreFor);

  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

/** Win rate as a whole percentage. A tie is half a win, as a net run rate table would have it. */
export function winRate(record: Record): number {
  if (record.played === 0) return 0;
  return Math.round(((record.won + record.tied * 0.5) / record.played) * 100);
}
